import { useState, useEffect } from "react";
import api from "@/lib/api";
import { money, fmtDate } from "@/lib/ui";
import { getChoices, pickPerf, toggleEnteredPerf } from "@/lib/choices";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { OfficialToggle } from "@/components/OfficialControls";
import { PickCircle } from "@/components/PickCircle";
import { ExternalLink, ChevronDown, ScrollText, Loader2 } from "lucide-react";
import { GroundRulesText } from "@/components/GroundRulesText";
import { ArenaCard, arenaInfo } from "@/components/ArenaCard";
import PurseBreakdown, { useEnteredCounts } from "@/components/PurseBreakdown";

function groundRulesText(pbj, rodeo) {
  const gr = pbj?.ground_rules || [];
  const listing = pbj?.listing;
  return (listing?.parsed?.ground_rules) || (gr[0]?.parsed?.ground_rules) || (gr[0]?.raw_text) || rodeo?.ground_rules || "";
}

const DLG = "bg-white text-[#14181F] border border-[#E4E7EB] w-[calc(100vw-1.5rem)] max-w-lg max-h-[88vh] overflow-y-auto overflow-x-hidden p-4 sm:p-6";

const MONTHS = { january:1,february:2,march:3,april:4,may:5,june:6,july:7,august:8,september:9,october:10,november:11,december:12,
  jan:1,feb:2,mar:3,apr:4,jun:6,jul:7,aug:8,sep:9,sept:9,oct:10,nov:11,dec:12 };

// Parse a free-text slack note ("June 29 th @ 10 am") into date/time so it renders like a perf row.
function parseSlackNote(note, year) {
  if (!note) return null;
  const mm = note.match(/(jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|jun(?:e)?|jul(?:y)?|aug(?:ust)?|sep(?:t)?(?:ember)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)\s*\.?\s*(\d{1,2})/i);
  if (!mm) return null;
  const mon = MONTHS[mm[1].toLowerCase()];
  if (!mon) return null;
  const day = parseInt(mm[2], 10);
  const tm = note.match(/(\d{1,2})(?::(\d{2}))?\s*([ap])\.?\s*m/i);
  let time = "";
  if (tm) time = `${parseInt(tm[1], 10)}:${tm[2] || "00"}${tm[3].toUpperCase()}M`;
  const par = note.match(/\(([^)]*)\)/);
  return { date: `${year}-${String(mon).padStart(2, "0")}-${String(day).padStart(2, "0")}`, time, extra: par ? par[1].trim() : "" };
}

// The PRCA rodeo number (e.g. 19177) lives in external_id ("prca-19177") or the
// performance ids ("prca-19177-p0"). Returns the number string, or null for CPRA/others.
function prcaNumber(r) {
  const ext = String(r?.external_id || "");
  let m = ext.match(/^prca-(\d+)/i);
  if (m) return m[1];
  for (const p of (r?.performances || [])) {
    const mm = String(p?.id || "").match(/^prca-(\d+)-/i);
    if (mm) return mm[1];
  }
  return null;
}
function prcaEntryUrl(r) {
  const n = prcaNumber(r);
  return n ? `https://rodeoentry.prorodeo.org/rodeo/${n}` : null;
}

function entryLink(r) {
  const direct = prcaEntryUrl(r);
  if (direct) return direct; // specific ProRodeo entry page when we know the number
  if ((r.associations || []).includes("PRCA")) return "https://www.prorodeo.org/";
  return r.website_url || r.draw_url || "https://cpralogin.com/login";
}

function InfoRow({ label, value }) {
  if (!value) return null;
  return (
    <div className="rounded-md bg-[#F7F8FA] px-3 py-2 min-w-0">
      <div className="text-[10px] font-bold uppercase tracking-wide text-slate-400">{label}</div>
      <div className="text-sm text-[#14181F] font-medium mt-0.5 break-words">{value}</div>
    </div>
  );
}

function MoneyTable({ title, rows, testid }) {
  if (!rows || !rows.length) return null;
  return (
    <div className="rounded-md border border-[#E4E7EB] p-3" data-testid={testid}>
      <div className="text-[10px] font-bold uppercase tracking-wide text-slate-400 mb-2">{title}</div>
      <div className="space-y-1">
        {rows.map((r, i) => (
          <div key={i} className="flex items-center justify-between text-sm py-1 px-2 rounded bg-[#F7F8FA]">
            <span className="text-slate-700">{r.event}</span>
            <span className="text-slate-900 font-semibold">{money(r.amount)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function RodeoInfoDialog({ data, onClose }) {
  const [showRaw, setShowRaw] = useState(false);
  const [full, setFull] = useState(null);
  const [tab, setTab] = useState(0);
  const [section, setSection] = useState("details");
  const [pbj, setPbj] = useState(null);
  const [pbjLoading, setPbjLoading] = useState(false);
  const [choices, setChoices] = useState(getChoices());
  const entered = useEnteredCounts(data?.id);
  useEffect(() => {
    setShowRaw(false);
    setFull(null);
    setTab(0);
    setSection("details");
    setPbj(null);
    setChoices(getChoices());
    if (data?.id) {
      api.get(`/rodeos/${data.id}`).then((r) => setFull(r.data)).catch(() => {});
      setPbjLoading(true);
      api.get(`/prca/rodeo/${data.id}/pbj`).then((r) => setPbj(r.data)).catch(() => setPbj(null)).finally(() => setPbjLoading(false));
    }
  }, [data?.id]);
  const d = full || data; // always prefer the fresh full record from the server
  const details = d?.sanction_details;
  const multi = Array.isArray(details) && details.length > 1;
  const s = multi ? (details[tab] || details[0]) : d; // per-association detail (CPRA vs PRCA) when dual-sanctioned
  // A rodeo sanctioned by CPRA (even if ALSO PRCA) is in Canada → entries go through the CPRA site.
  const isCPRA = (d?.associations || []).includes("CPRA");
  const perfs = s?.performances || [];
  const hasSlackRow = perfs.some((p) => p.type === "slack");
  const slackNote = s?.slack_info && !hasSlackRow ? s.slack_info : null; // conditional slack lives only in text
  const refYear = ((perfs.find((p) => p.date) || {}).date || d?.start_date || "").slice(0, 4) || String(new Date().getFullYear());
  // Arena/grounds name + street address from the scraped PBJ listing.
  const _arena = arenaInfo(pbj);
  const _groundsName = s?.rodeo_grounds || s?.arena || _arena?.name || "";
  const _groundsAddr = _arena?.address ? [_arena.address, _arena.location].filter(Boolean).join(", ") : null;
  const _groundsMaps = _groundsName ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([_groundsName, _arena?.address, _arena?.location].filter(Boolean).join(", "))}` : null;
  const groundsNode = _groundsName ? (
    <span>
      {_groundsName}
      {_groundsAddr && <span className="block text-[12px] text-slate-500 font-normal mt-0.5" data-testid="info-grounds-address">{_groundsAddr}</span>}
      {_groundsAddr && <a href={_groundsMaps} target="_blank" rel="noreferrer" data-testid="info-grounds-directions" className="inline-flex items-center gap-1 mt-1 text-[12px] font-semibold text-[#C8000F] hover:underline">Get directions</a>}
    </span>
  ) : null;
  const slackParsed = slackNote ? parseSlackNote(slackNote, refYear) : null;
  // Direct ProRodeo entry page derived from the PRCA number (prefer the full record `d`).
  const _prcaNum = prcaNumber(d);
  const _prcaEntry = _prcaNum ? `https://rodeoentry.prorodeo.org/rodeo/${_prcaNum}` : null;
  // When opened with just {id,name} (e.g. from Today/ticker), hold a loader until the full
  // record arrives so the body doesn't flash from a near-empty grid to the populated one.
  const bodyReady = !!(full || data?.city || (data?.performances && data.performances.length) || data?.sanction_details);
  return (
    <Dialog open={!!data} onOpenChange={onClose}>
      <DialogContent className={DLG} data-testid="rodeo-info-dialog">
        {d && (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-lg sm:text-2xl pr-6 break-words">{d.name}</DialogTitle>
              <DialogDescription className="break-words">How to enter, ground rules & performance schedule</DialogDescription>
            </DialogHeader>
            <div className="flex gap-1.5 border-b border-[#EEF0F2] -mt-1" data-testid="info-section-tabs">
              {[["details", "Details"], ["rules", "Ground Rules"]].map(([k, lbl]) => (
                <button key={k} onClick={() => setSection(k)} data-testid={`info-section-${k}`}
                  className={`text-sm font-bold px-3 py-2 -mb-px border-b-2 transition-colors ${section === k ? "border-[#C8000F] text-[#C8000F]" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
                  {lbl}
                </button>
              ))}
            </div>
            {!bodyReady ? (
              <div className="flex items-center gap-2 text-sm text-slate-500 py-20 justify-center" data-testid="info-loading"><Loader2 className="w-5 h-5 animate-spin" /> Loading rodeo…</div>
            ) : section === "rules" ? (
              <div className="space-y-3" data-testid="info-rules-panel">
                <ArenaCard pbj={pbj} />
                {_prcaEntry && (
                  <a href={_prcaEntry} target="_blank" rel="noreferrer" data-testid="info-rules-enter-link"
                    className="flex items-center justify-center gap-2 w-full bg-[#C8000F] hover:bg-[#9E0008] text-white text-sm font-semibold py-2.5 rounded-lg">
                    <ExternalLink className="w-4 h-4" /> Enter on ProRodeo · Rodeo #{_prcaNum}
                  </a>
                )}
                {pbjLoading && !pbj ? (
                  <div className="flex items-center gap-2 text-sm text-slate-500 py-8 justify-center"><Loader2 className="w-4 h-4 animate-spin" /> Loading ground rules…</div>
                ) : groundRulesText(pbj, d) ? (
                  <>
                    <div className="rounded-md border border-[#E4E7EB] p-3">
                      <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide text-slate-400 mb-1.5"><ScrollText className="w-3.5 h-3.5" /> Ground rules</div>
                      <GroundRulesText text={groundRulesText(pbj, d)} />
                    </div>
                    {pbj?.source && (pbj?.ground_rules?.length || pbj?.listing?.parsed?.ground_rules) && (
                      <div className="text-[11px] text-slate-400">Source: {pbj.source}</div>
                    )}
                  </>
                ) : (
                  <div className="text-sm text-slate-500 py-8 text-center" data-testid="info-rules-empty">No ground rules posted yet for this rodeo. They're pulled from the PRCA PBJ listing and appear here once published.</div>
                )}
              </div>
            ) : (
            <>
            {multi && (
              <div className="flex gap-1.5" data-testid="sanction-tabs">
                {details.map((x, i) => (
                  <button key={i} onClick={() => setTab(i)} data-testid={`sanction-tab-${x.assoc}`}
                    className={`text-xs font-bold px-3 py-1.5 rounded-md border transition-colors ${tab === i ? "bg-[#0C1017] text-white border-[#0C1017]" : "bg-white text-slate-600 border-[#D7DBE0] hover:border-slate-400"}`}>
                    {x.assoc}
                  </button>
                ))}
              </div>
            )}
            <div className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <InfoRow label="Location" value={[d.city, d.state || d.province].filter(Boolean).join(", ")} />
                <InfoRow label={multi ? "Sanction" : "Associations"} value={multi ? s.assoc : (d.associations || []).join(", ")} />
                <InfoRow label="Added money" value={s.added_money ? money(s.added_money) : ""} />
                <InfoRow label="Total entered" value={entered?.total ? String(entered.total) : ""} />
                <InfoRow label="Arena" value={s.arena} />
                <InfoRow label="Entries open" value={fmtDate(s.entry_open)} />
                <InfoRow label="Entries close" value={fmtDate(s.entry_close)} />
                <InfoRow label="Committee contact" value={s.committee_contact} />
                <InfoRow label="Rodeo office" value={s.rodeo_office} />
                <InfoRow label="Rodeo grounds" value={groundsNode} />
                <InfoRow label="Stock contractor" value={s.main_stock} />
                <InfoRow label="Timed-event stock" value={s.timed_stock} />
                <InfoRow label="Circuit" value={s.circuit_name} />
              </div>

              {(s.entry_info || s.how_to_enter) && (
                <div className="rounded-md border border-[#E4E7EB] p-3" data-testid="info-how-to-enter">
                  <div className="text-[10px] font-bold uppercase tracking-wide text-slate-400 mb-1">How to enter</div>
                  <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-line">{s.entry_info || s.how_to_enter}</p>
                </div>
              )}

              <PurseBreakdown rodeo={s} entered={entered} testid="info-purses" />
              <MoneyTable title="Entry fees by event" rows={s.entry_fees} testid="info-entry-fees" />

              {(s.perf_order || s.slack_order) && (
                <div className="rounded-md bg-[#F7F8FA] px-3 py-2 text-sm text-slate-600 space-y-1" data-testid="info-order">
                  {s.perf_order && <div><span className="font-semibold text-slate-500">Performance order:</span> {s.perf_order}</div>}
                  {s.slack_info && <div><span className="font-semibold text-slate-500">Slack:</span> {s.slack_info}</div>}
                  {s.slack_order && <div><span className="font-semibold text-slate-500">Slack order:</span> {s.slack_order}</div>}
                </div>
              )}

              {s.ground_rules && (
                <div className="rounded-md border border-[#E4E7EB] p-3" data-testid="info-ground-rules">
                  <div className="text-[10px] font-bold uppercase tracking-wide text-slate-400 mb-1">Ground rules</div>
                  <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-line">{s.ground_rules}</p>
                </div>
              )}

              {(perfs.length > 0 || slackNote) && (
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <div className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Performances & slack</div>
                    <div className="text-[10px] text-slate-400">Tap <span className="text-[#C8000F] font-bold">⊕</span> to set 1st / 2nd choice</div>
                  </div>
                  {perfs.map((p) => {
                    const ch = choices[d.id] || {};
                    const num = ch.pref1 === p.id ? 1 : ch.pref2 === p.id ? 2 : null;
                    const ent = !!(ch.entered || {})[p.id];
                    return (
                    <div key={p.id} data-testid={`info-perf-row-${p.id}`} className={`flex items-center justify-between text-sm py-1.5 px-3 rounded-md ${ent ? "bg-[#E9F6EC] ring-1 ring-[#16A34A]/40" : num ? "bg-[#EAF3EC] ring-1 ring-[#166534]/30" : "bg-[#F7F8FA]"}`}>
                      <span className="flex items-center gap-2.5 min-w-0">
                        <PickCircle num={num} entered={ent} onClick={() => setChoices(pickPerf(d.id, p.id))} onToggleEntered={() => setChoices(toggleEnteredPerf(d.id, p.id))} testid={`info-pick-${p.id}`} />
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${p.type === "slack" ? "bg-purple-100 text-purple-700" : "bg-blue-100 text-blue-700"}`}>{p.type === "slack" ? "SLACK" : "PERF"}</span>
                        <span className="truncate">{p.label}</span>
                      </span>
                      <span className="text-slate-500 shrink-0 pl-2">{fmtDate(p.date)}{p.time ? ` · ${p.time}` : ""}</span>
                    </div>
                    );
                  })}
                  {slackNote && slackParsed && (
                    <div className="flex items-center justify-between text-sm py-1.5 px-3 rounded-md bg-[#F7F8FA]" data-testid="info-slack-note-row">
                      <span className="flex items-center gap-2">
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-purple-100 text-purple-700">SLACK</span>
                        {slackParsed.extra && <span className="text-slate-500">{slackParsed.extra}</span>}
                      </span>
                      <span className="text-slate-500">{fmtDate(slackParsed.date)}{slackParsed.time ? ` · ${slackParsed.time}` : ""}</span>
                    </div>
                  )}
                  {slackNote && !slackParsed && (
                    <div className="flex items-start gap-2 text-sm py-1.5 px-3 rounded-md bg-[#F7F8FA]" data-testid="info-slack-note-row">
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-purple-100 text-purple-700 shrink-0 mt-0.5">SLACK</span>
                      <span className="text-slate-600 leading-snug">{slackNote}</span>
                    </div>
                  )}
                </div>
              )}

              <OfficialToggle rodeoId={d.id} />

              {s.status !== "closed" && !isCPRA && (
                <a href={_prcaEntry || entryLink(s)} target="_blank" rel="noreferrer" data-testid="info-enter-link"
                  className="flex items-center justify-center gap-2 w-full bg-[#C8000F] hover:bg-[#9E0008] text-white text-sm font-semibold py-2.5 rounded-lg">
                  <ExternalLink className="w-4 h-4" /> {_prcaEntry ? `Enter on ProRodeo · Rodeo #${_prcaNum}` : "Enter online / official page"}
                </a>
              )}
              {isCPRA && (
                <>
                  <a href="https://cpralogin.com/login" target="_blank" rel="noreferrer" data-testid="info-cpra-enter-link"
                    className="flex items-center justify-center gap-2 w-full bg-[#C8000F] hover:bg-[#9E0008] text-white text-sm font-semibold py-2.5 rounded-lg">
                    <ExternalLink className="w-4 h-4" /> Enter on CPRA site
                  </a>
                  <p className="text-center text-xs text-slate-500 mt-1.5" data-testid="info-cpra-note">
                    CPRA entries are taken on the CPRA site (cpralogin.com).
                  </p>
                </>
              )}

              {s.details_text && (
                <div className="rounded-md border border-[#E4E7EB]" data-testid="info-full-details">
                  <button onClick={() => setShowRaw((v) => !v)} data-testid="info-full-details-toggle"
                    className="flex items-center justify-between w-full px-3 py-2.5 text-sm font-semibold text-slate-700">
                    <span>Full rodeo details{multi ? ` (${s.assoc})` : " (from CPRA)"}</span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${showRaw ? "rotate-180" : ""}`} />
                  </button>
                  {showRaw && (
                    <p className="px-3 pb-3 text-sm text-slate-600 leading-relaxed whitespace-pre-line border-t border-[#EEF0F2] pt-2">{s.details_text}</p>
                  )}
                </div>
              )}
            </div>
            </>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
